// Parse a video link (YouTube / TikTok / Facebook / Vimeo / Instagram) into
// thumbnail + embed URLs **and the real aspect ratio**, so a community card can
// follow the video: a 16:9 link renders a 16:9 card, a 9:16 (Shorts / Reel /
// TikTok) link renders a 9:16 card, always with a thumbnail on the card.
//
// Ratio detection:
//   Â· /shorts/, /reel/, /reels/, tiktok.com ............ 9:16 (portrait)
//   Â· instagram /p/ ................................... 4:5
//   Â· everything else ................................ 16:9 (landscape)
//   Â· any link can force it: ?ratio=9:16 | ?ratio=vertical | ?ratio=16:9
export function parseVideo(rawUrl) {
  const url = String(rawUrl || '').trim();
  const out = {
    type: 'link', id: '', thumb: '', thumbFallback: '', embed: '', autoplay: '',
    host: '', ratio: '16 / 9', ratioKey: '16:9', portrait: false,
    preview: 'thumb',   // thumb | iframe â€” how the card shows the preview
  };
  if (!url) return out;

  let u;
  try {
    u = new URL(/^https?:\/\//i.test(url) ? url : 'https://' + url);
  } catch (e) {
    return out;
  }
  const host = u.hostname.replace(/^www\./, '').toLowerCase();
  const path = u.pathname || '';
  out.host = host;

  // Explicit ratio hint in the link (ratio=9:16 / vertical / portrait / short).
  const hint = `${u.searchParams.get('ratio') || ''} ${u.searchParams.get('orientation') || ''}`.toLowerCase();
  const forceVertical = /9[:x/]16|vertical|portrait|short/.test(hint);
  const forceWide = /16[:x/]9|horizontal|landscape|wide/.test(hint);

  /** Attach the detected ratio to a result. */
  const withRatio = (obj, ratio, ratioKey) => {
    let key = ratioKey;
    if (forceVertical) key = '9:16';
    if (forceWide) key = '16:9';
    const map = { '9:16': '9 / 16', '4:5': '4 / 5', '1:1': '1 / 1', '16:9': '16 / 9' };
    return {
      ...obj,
      ratioKey: key,
      ratio: map[key] || map['16:9'],
      portrait: key === '9:16' || key === '4:5',
    };
  };

  const ytId = (() => {
    if (host === 'youtu.be') return path.slice(1).split('/')[0];
    if (host.endsWith('youtube.com')) {
      if (u.searchParams.get('v')) return u.searchParams.get('v');
      const m = path.match(/\/(embed|shorts|live)\/([\w-]+)/);
      if (m) return m[2];
    }
    return '';
  })();
  if (ytId) {
    const vertical = /\/shorts\//.test(path) || forceVertical;
    return withRatio({
      ...out,
      type: 'youtube',
      id: ytId,
      thumb: `https://i.ytimg.com/vi/${ytId}/maxresdefault.jpg`,
      thumbFallback: `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`,
      embed: `https://www.youtube.com/embed/${ytId}?rel=0`,
      autoplay: `https://www.youtube.com/embed/${ytId}?rel=0&autoplay=1&mute=1`,
      preview: 'thumb',
    }, vertical ? '9:16' : '16:9', vertical ? '9:16' : '16:9');
  }

  const vimeoId = host.endsWith('vimeo.com') ? (path.match(/\/(\d+)/) || [])[1] : '';
  if (vimeoId) {
    return withRatio({
      ...out,
      type: 'vimeo',
      id: vimeoId,
      thumb: `https://vumbnail.com/${vimeoId}.jpg`,
      embed: `https://player.vimeo.com/video/${vimeoId}`,
      autoplay: `https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=1`,
      preview: 'thumb',
    }, '16:9', '16:9');
  }

  if (host.endsWith('tiktok.com')) {
    const ttId = (path.match(/\/video\/(\d+)/) || [])[1]
      || (path.match(/\/photo\/(\d+)/) || [])[1]
      || '';
    return withRatio({
      ...out,
      type: 'tiktok',
      id: ttId,
      thumb: '',
      embed: ttId ? `https://www.tiktok.com/embed/v2/${ttId}` : '',
      autoplay: ttId ? `https://www.tiktok.com/embed/v2/${ttId}?autoplay=1` : '',
      // TikTok owns its poster frame â€” the embed itself is the thumbnail.
      preview: ttId ? 'iframe' : 'thumb',
    }, '9:16', '9:16');
  }

  if (host.endsWith('facebook.com') || host === 'fb.watch') {
    const vertical = /\/(reel|reels)\//.test(path) || forceVertical;
    return withRatio({
      ...out,
      type: 'facebook',
      thumb: '',
      embed: `https://www.facebook.com/plugins/video.php?show_text=false&width=734&href=${encodeURIComponent(url)}`,
      autoplay: `https://www.facebook.com/plugins/video.php?autoplay=true&show_text=false&href=${encodeURIComponent(url)}`,
      preview: 'iframe',
    }, vertical ? '9:16' : '16:9', vertical ? '9:16' : '16:9');
  }

  const igMatch = path.match(/\/(p|reel|reels|tv)\/([\w-]+)/);
  if (host.endsWith('instagram.com') && igMatch) {
    const kind = igMatch[1] === 'reels' ? 'reel' : igMatch[1];
    const vertical = kind === 'reel' || forceVertical;
    return withRatio({
      ...out,
      type: 'instagram',
      id: igMatch[2],
      thumb: '',
      embed: `https://www.instagram.com/${kind}/${igMatch[2]}/embed`,
      autoplay: `https://www.instagram.com/${kind}/${igMatch[2]}/embed`,
      // Reels are vertical, feed posts are square-ish.
      preview: 'iframe',
    }, vertical ? '9:16' : '4:5', vertical ? '9:16' : '4:5');
  }

  return out;
}

/** Convenience: just the CSS aspect-ratio for a link. */
export const videoRatio = (url) => parseVideo(url).ratio;

/**
 * A video that plays anywhere (a direct .mp4/.webm/.mov link or a data URL) â€”
 * these can be rendered by a plain <video> tag instead of an iframe.
 */
export function isDirectVideo(url) {
  const raw = String(url || '').trim();
  if (!raw) return false;
  if (/^data:video\//i.test(raw)) return true;
  return /\.(mp4|webm|ogv|ogg|mov|m4v)(\?.*)?$/i.test(raw);
}

export const VIDEO_LABEL = {
  youtube: 'YouTube',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  vimeo: 'Vimeo',
  instagram: 'Instagram',
  link: 'Video',
};
